import React, { useState, useEffect, useRef } from 'react';
import { format, parseISO } from 'date-fns';
import { Search, X, Download, ChevronDown } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { ReportChartRow, BarCard, DonutCard } from './ReportCharts';

const MAX_QUALS = 15;

const STATUS_MAP = {
  expired:      { label: 'Expired',      bg: 'bg-red-500',   text: 'text-white' },
  expiring_soon:{ label: 'Expiring',     bg: 'bg-amber-400', text: 'text-white' },
  unverified:   { label: 'Unverified',   bg: 'bg-cyan-400',  text: 'text-white' },
  valid:        { label: 'Valid',         bg: 'bg-green-500', text: 'text-white' },
  none:         { label: '—',            bg: '',             text: 'text-slate-400' },
};

function StatusBadge({ status }) {
  const s = STATUS_MAP[status] || STATUS_MAP.none;
  if (status === 'none') return <span className="text-slate-400 text-sm">—</span>;
  return (
    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-semibold ${s.bg} ${s.text}`}>
      {s.label}
    </span>
  );
}

export default function StaffCompliance() {
  const [categories,    setCategories]    = useState([]);
  const [allQuals,      setAllQuals]      = useState([]);
  const [category,      setCategory]      = useState('');
  const [selectedQuals, setSelectedQuals] = useState([]);
  const [search,        setSearch]        = useState('');
  const [showDropdown,  setShowDropdown]  = useState(false);
  const [data,          setData]          = useState(null);
  const [loading,       setLoading]       = useState(false);
  const dropRef = useRef(null);

  // Load categories and all qual definitions from smart-match
  useEffect(() => {
    api.get('/smart-match').then(r => {
      setCategories(r.data.categories || []);
      setAllQuals(r.data.qualifications || []);
    }).catch(() => toast.error('Failed to load qualification categories'));
  }, []);

  // Load compliance matrix whenever selected quals change
  useEffect(() => {
    if (!selectedQuals.length) { setData(null); return; }
    setLoading(true);
    const ids = selectedQuals.map(q => q.id).join(',');
    api.get(`/reports/staff-compliance?qual_ids=${ids}`)
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load compliance data'))
      .finally(() => setLoading(false));
  }, [selectedQuals]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = e => { if (dropRef.current && !dropRef.current.contains(e.target)) setShowDropdown(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filteredQuals = allQuals.filter(q => {
    const matchCat = !category || q.category_id === category;
    const matchSearch = !search || q.name.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch && q.is_active;
  });

  function toggleQual(qual) {
    setSelectedQuals(prev => {
      if (prev.find(q => q.id === qual.id)) return prev.filter(q => q.id !== qual.id);
      if (prev.length >= MAX_QUALS) { toast.error(`Max ${MAX_QUALS} qualifications`); return prev; }
      return [...prev, qual];
    });
  }

  function exportCSV() {
    if (!data?.staff?.length) return;
    const headers = ['Staff', ...data.quals.map(q => q.name)];
    const rows = data.staff.map(s => [
      s.name,
      ...data.quals.map(q => {
        const cell = s.quals[q.id];
        if (!cell || cell.status === 'none') return 'No qualification';
        return `${STATUS_MAP[cell.status]?.label || cell.status}${cell.expiry_date ? ' ('+cell.expiry_date.slice(0,10)+')' : ''}`;
      })
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `staff_compliance_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
  }

  const quals = data?.quals || selectedQuals;
  const staff = data?.staff || [];

  // Aggregate the matrix cells + per-qual coverage for the summary charts.
  const complianceCells = (() => {
    if (!staff.length || !quals.length) return [];
    let valid = 0, expiring = 0, expired = 0, unverified = 0, none = 0;
    staff.forEach(s => quals.forEach(q => {
      const st = s.quals?.[q.id]?.status || 'none';
      if (st === 'valid') valid++;
      else if (st === 'expiring_soon') expiring++;
      else if (st === 'expired') expired++;
      else if (st === 'unverified') unverified++;
      else none++;
    }));
    return [
      { name: 'Valid', value: valid }, { name: 'Expiring', value: expiring },
      { name: 'Expired', value: expired }, { name: 'Unverified', value: unverified },
      { name: 'No qual', value: none },
    ];
  })();
  const coverageByQual = quals.map(q => ({
    name: q.name,
    value: staff.filter(s => s.quals?.[q.id]?.status === 'valid').length,
  })).sort((a, b) => b.value - a.value).slice(0, 10);

  return (
    <div className="p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-sm text-slate-500 font-medium">Report</p>
          <h1 className="text-2xl font-bold text-slate-800">Staff Compliance Index</h1>
        </div>
        <button onClick={exportCSV} disabled={!data?.staff?.length}
          className="bg-cyan-500 hover:bg-cyan-600 disabled:opacity-40 text-white font-semibold px-6 py-2 rounded-lg text-sm transition-colors flex items-center gap-2">
          <Download size={14}/> Export CSV
        </button>
      </div>

      <div className="space-y-5">
        {/* Filters */}
        <div className="space-y-4">
          {/* Category */}
          <div className="w-72">
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Category</label>
            <div className="relative">
              <select value={category} onChange={e => { setCategory(e.target.value); setSelectedQuals([]); setSearch(''); }}
                className="w-full border border-slate-300 rounded px-3 py-2 text-sm bg-white text-slate-700 appearance-none focus:outline-none focus:ring-2 focus:ring-cyan-400 pr-8">
                <option value="">All Categories</option>
                {categories.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              {category ? (
                <button onClick={() => { setCategory(''); setSelectedQuals([]); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  <X size={14}/>
                </button>
              ) : (
                <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"/>
              )}
            </div>
          </div>

          {/* Qualification multi-select */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Qualification</label>
            <div className="flex items-start gap-4">
              <div className="relative w-96" ref={dropRef}>
                <div className="flex items-center border border-slate-300 rounded bg-white px-3 py-2 gap-2 focus-within:ring-2 focus-within:ring-cyan-400">
                  <Search size={14} className="text-slate-400 shrink-0"/>
                  <input
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    onFocus={() => setShowDropdown(true)}
                    placeholder={`Search and select qualifications (max ${MAX_QUALS})`}
                    className="flex-1 text-sm focus:outline-none bg-transparent text-slate-700 placeholder-slate-400"
                  />
                </div>
                {showDropdown && (
                  <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-xl z-50 max-h-64 overflow-y-auto">
                    {filteredQuals.length === 0 ? (
                      <p className="px-4 py-3 text-sm text-slate-400">No qualifications found</p>
                    ) : filteredQuals.map(q => {
                      const selected = !!selectedQuals.find(s => s.id === q.id);
                      return (
                        <button key={q.id} onClick={() => toggleQual(q)}
                          className={`w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 transition-colors flex items-center justify-between ${selected ? 'text-cyan-600 font-semibold' : 'text-slate-700'}`}>
                          <span>{q.name}</span>
                          {selected && <span className="w-4 h-4 bg-cyan-500 rounded-full shrink-0"/>}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              <span className="text-sm text-slate-500 pt-2">
                {selectedQuals.length} of {MAX_QUALS} selected
              </span>
            </div>

            {/* Selected qual chips */}
            {selectedQuals.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {selectedQuals.map(q => (
                  <span key={q.id} className="inline-flex items-center gap-1.5 bg-cyan-50 border border-cyan-200 text-cyan-700 text-xs font-medium px-2.5 py-1 rounded-full">
                    {q.name}
                    <button onClick={() => toggleQual(q)} className="hover:text-red-500"><X size={11}/></button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-6 text-sm text-slate-600">
          <div className="flex items-center gap-1.5">
            <span className="inline-flex px-2 py-0.5 rounded text-xs font-semibold bg-amber-400 text-white">Expiring</span>
            <span>expires ≤ 30 days</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="inline-flex px-2 py-0.5 rounded text-xs font-semibold bg-red-500 text-white">Expired</span>
            <span>Past expiry date</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="inline-flex px-2 py-0.5 rounded text-xs font-semibold bg-cyan-400 text-white">Unverified</span>
            <span>Not verified, not expired</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-slate-400 font-bold">—</span>
            <span>No qualification</span>
          </div>
        </div>

        <div className="border-t border-slate-200"/>

        {/* Charts */}
        {complianceCells.some(c => c.value > 0) && (
          <ReportChartRow>
            <DonutCard title="Compliance across all cells" data={complianceCells} colors={['#10B981', '#F59E0B', '#EF4444', '#06B6D4', '#94A3B8']} />
            <BarCard title="Valid staff per qualification" data={coverageByQual} color="#10B981" horizontal />
          </ReportChartRow>
        )}

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-x-auto">
          {loading ? (
            <div className="py-20 flex items-center justify-center">
              <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin"/>
            </div>
          ) : !selectedQuals.length ? (
            <p className="py-20 text-center text-slate-400 text-sm">Please select a qualification</p>
          ) : !staff.length ? (
            <p className="py-20 text-center text-slate-400 text-sm">No staff records found</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-600 whitespace-nowrap sticky left-0 bg-slate-50 min-w-[160px]">
                    Staff
                  </th>
                  {quals.map(q => (
                    <th key={q.id} className="px-4 py-3 text-xs font-semibold text-slate-600 text-center whitespace-nowrap min-w-[140px]">
                      {q.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {staff.map((s, i) => (
                  <tr key={s.id} className={`border-b border-slate-100 hover:bg-slate-50/50 ${i % 2 === 1 ? 'bg-slate-50/30' : ''}`}>
                    <td className="px-5 py-3 font-medium text-slate-800 whitespace-nowrap sticky left-0 bg-inherit">
                      {s.name}
                    </td>
                    {quals.map(q => {
                      const cell = s.quals?.[q.id];
                      const status = cell?.status || 'none';
                      return (
                        <td key={q.id} className="px-4 py-3 text-center whitespace-nowrap">
                          <div className="flex flex-col items-center gap-0.5">
                            <StatusBadge status={status}/>
                            {cell?.expiry_date && status !== 'none' && (
                              <span className="text-[10px] text-slate-400">
                                {cell.expiry_date.slice(0, 10)}
                              </span>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
